import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Alert from '../../components/common/Alert';
import Loading from '../../components/common/Loading';
import AddressEditor from '../../components/employee/AddressEditor';
import {
  updateProfile,
  getAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
  setDefaultAddress,
  getCompanyMembers,
  inviteTeamMember,
  getBillingContacts,
  addBillingContact,
  deleteBillingContact,
  setPrimaryBillingContact,
} from '../../services/customers';
import api from '../../services/api';
import { validateRequired, validatePhone, validateEmail, getPasswordValidation } from '../../utils/validators';
import { formatPhoneInput } from '../../utils/formatters';
import './Profile.css';

// Must stay in sync with ALLOWED_BUSINESS_TYPES in backend/apps/users/views.py
const BUSINESS_TYPE_OPTIONS = [
  'Bakery',
  'Restaurant',
  'Retailer',
  'Distributor',
  'Other',
];

/**
 * Profile page - edit customer profile information
 */
const Profile = () => {
  const { profile, user, refreshProfile } = useAuth();
  // Show the incomplete notice whenever the profile is actually incomplete
  // (handles both flat login shape and nested ProfileSerializer shape)
  const profileIsIncomplete =
    profile?.company?.company_profile_complete === false ||
    profile?.company_profile_complete === false;

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    phone: '',
    business_types: [],
    business_type_other: '',
    resale_tax_id: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // ── Change password state ───────────────────────────────────
  const [pwData, setPwData] = useState({ current: '', newPw: '', confirm: '' });
  const [pwErrors, setPwErrors] = useState({});
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwError, setPwError] = useState('');

  // ── Address state ────────────────────────────────────────────
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [addressError, setAddressError] = useState('');

  // ── Billing contacts state ───────────────────────────────────
  const [billingContacts, setBillingContacts] = useState([]);
  const [newContact, setNewContact] = useState({ name: '', email: '' });
  const [addingContact, setAddingContact] = useState(false);
  const [contactError, setContactError] = useState('');
  const [showAddContact, setShowAddContact] = useState(false);

  // ── Team members state ───────────────────────────────────────
  const [members, setMembers] = useState([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteData, setInviteData] = useState({ first_name: '', last_name: '', email: '' });
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  // company_id is available on both the flat (login) and full (ProfileSerializer) shapes
  const companyId = profile?.company_id ?? profile?.company?.id;

  // Ensure we have the full profile shape (with nested company.phone) on mount.
  // login/signup stores a flat profile; ProfileView returns the full nested shape.
  useEffect(() => {
    refreshProfile().catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAddresses = useCallback(async (signal) => {
    if (!companyId) return;
    setAddressesLoading(true);
    try {
      const data = await getAddresses(companyId);
      if (signal?.aborted) return;
      const all = Array.isArray(data) ? data : (data.results ?? []);
      setAddresses(all);
    } catch {
      if (signal?.aborted) return;
      setAddresses([]);
    } finally {
      if (!signal?.aborted) setAddressesLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    const controller = new AbortController();
    fetchAddresses(controller.signal);
    return () => controller.abort();
  }, [fetchAddresses]);

  const fetchMembers = useCallback(() => {
    getCompanyMembers()
      .then(setMembers)
      .catch(() => setMembers([]));
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const fetchBillingContacts = useCallback(() => {
    getBillingContacts()
      .then(setBillingContacts)
      .catch(() => setBillingContacts([]));
  }, []);

  useEffect(() => {
    fetchBillingContacts();
  }, [fetchBillingContacts]);

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');

    if (!validateEmail(inviteData.email)) {
      setInviteError('Enter a valid email address.');
      return;
    }

    setInviteSubmitting(true);
    try {
      await inviteTeamMember(inviteData);
      setInviteSuccess(`Invite sent to ${inviteData.email}.`);
      setInviteData({ first_name: '', last_name: '', email: '' });
      setShowInviteForm(false);
      fetchMembers();
    } catch (err) {
      setInviteError(err?.message || 'Failed to send invite. Please try again.');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    setContactError('');
    if (!newContact.name.trim()) {
      setContactError('Name is required.');
      return;
    }
    if (!validateEmail(newContact.email)) {
      setContactError('Enter a valid email address.');
      return;
    }
    setAddingContact(true);
    try {
      await addBillingContact(newContact);
      setNewContact({ name: '', email: '' });
      setShowAddContact(false);
      fetchBillingContacts();
      refreshProfile().catch(() => {});
    } catch (err) {
      setContactError(err?.message || 'Failed to add contact.');
    } finally {
      setAddingContact(false);
    }
  };

  const handleDeleteContact = async (id) => {
    setContactError('');
    try {
      await deleteBillingContact(id);
      fetchBillingContacts();
      refreshProfile().catch(() => {});
    } catch (err) {
      setContactError(err?.message || 'Failed to remove contact.');
    }
  };

  const handleSetPrimary = async (id) => {
    setContactError('');
    try {
      await setPrimaryBillingContact(id);
      fetchBillingContacts();
    } catch (err) {
      setContactError(err?.message || 'Failed to update primary contact.');
    }
  };

  /**
   * Shared wrapper for address mutation handlers:
   * sets saving state, calls the async operation, refreshes the list,
   * and surfaces any error to the user instead of silently swallowing it.
   */
  const withAddressSave = useCallback(async (fn) => {
    setIsSavingAddress(true);
    setAddressError('');
    try {
      await fn();
      await fetchAddresses();
      // Refresh auth profile so company_profile_complete updates everywhere
      refreshProfile().catch(() => {});
    } catch (err) {
      setAddressError(err?.message || 'Failed to save address. Please try again.');
    } finally {
      setIsSavingAddress(false);
    }
  }, [fetchAddresses, refreshProfile]);

  const handleAddAddress = (addressData) =>
    withAddressSave(() => createAddress(addressData));

  const handleEditAddress = (addressId, addressData) =>
    withAddressSave(() => updateAddress(addressId, addressData));

  const handleDeleteAddress = (addressId) =>
    withAddressSave(() => deleteAddress(addressId));

  const handleSetDefault = (addressId) =>
    withAddressSave(() => setDefaultAddress(addressId));

  // Initialize form with current profile data.
  // Handles two possible profile shapes:
  //   - Flat  (login/signup):   profile.company_name, no phone
  //   - Full  (ProfileView GET): profile.company.company_name, profile.company.phone
  useEffect(() => {
    if (user && profile) {
      const company = profile.company ?? {};
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        company_name: company.company_name ?? profile.company_name ?? '',
        phone: formatPhoneInput(company.phone ?? ''),
        business_types: Array.isArray(company.business_types) ? company.business_types : [],
        business_type_other: company.operation_type_other ?? '',
        resale_tax_id: company.resale_tax_id ?? '',
      });
    }
  }, [user, profile]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Clear messages
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleBusinessTypeToggle = (type) => {
    setFormData(prev => {
      const current = prev.business_types;
      const next = current.includes(type)
        ? current.filter(t => t !== type)
        : [...current, type];
      return { ...prev, business_types: next };
    });
    setSuccessMessage('');
    setErrorMessage('');
  };

  const validate = () => {
    const newErrors = {};

    if (!validateRequired(formData.first_name)) {
      newErrors.first_name = 'First name is required';
    }
    if (!validateRequired(formData.last_name)) {
      newErrors.last_name = 'Last name is required';
    }
    if (!validateRequired(formData.company_name)) {
      newErrors.company_name = 'Company name is required';
    }
    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (formData.business_types.includes('Other') && !formData.business_type_other.trim()) {
      newErrors.business_type_other = 'Please describe your business type';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Change password handler ─────────────────────────────────
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const errs = {};

    if (!validateRequired(pwData.current)) errs.current = 'Current password is required';
    if (!validateRequired(pwData.newPw)) {
      errs.newPw = 'New password is required';
    } else {
      const v = getPasswordValidation(pwData.newPw);
      if (!v.isValid) errs.newPw = v.errors[0];
    }
    if (!validateRequired(pwData.confirm)) {
      errs.confirm = 'Please confirm your new password';
    } else if (pwData.newPw !== pwData.confirm) {
      errs.confirm = 'Passwords do not match';
    }

    setPwErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setPwSubmitting(true);
    setPwError('');
    setPwSuccess('');

    try {
      await api.post('/auth/change-password/', {
        current_password: pwData.current,
        new_password: pwData.newPw,
      });
      setPwSuccess('Password updated successfully.');
      setPwData({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      const data = err.response?.data;
      if (Array.isArray(data?.error)) {
        setPwError(data.error.join(' '));
      } else if (data?.error) {
        setPwError(data.error);
      } else {
        setPwError('Failed to update password. Please try again.');
      }
    } finally {
      setPwSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage('');
      setSuccessMessage('');

      // Update profile
      await updateProfile(formData);

      // Refresh profile data in context
      await refreshProfile();

      setSuccessMessage('Profile updated successfully!');

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Failed to update profile:', error);
      setErrorMessage(
        error.message || 'Failed to update profile. Please try again.'
      );

      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dynamically compute which required fields are still missing.
  // Reactive to formData (updates as user types) and addresses list.
  const completionChecklist = [
    { label: 'Phone number',          done: !!formData.phone.trim() },
    { label: 'Business type',         done: formData.business_types.length > 0 },
    { label: 'Billing contact',        done: billingContacts.some(c => c.is_primary) },
    { label: 'Shipping address',      done: addresses.some(a => a.address_type === 'shipping') },
    { label: 'Billing address',       done: addresses.some(a => a.address_type === 'billing') },
  ];

  if (!profile || !user) {
    return (
      <div className="profile-page profile-page--loading">
        <Loading />
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Header */}
      <div className="profile-page__header">
        <h1 className="profile-page__title">My Profile</h1>
        <p className="profile-page__subtitle">Manage your account information</p>
      </div>

      {/* Profile incomplete checklist — shown whenever the profile is incomplete */}
      {profileIsIncomplete && (
        <div className="profile-page__incomplete-notice">
          <div className="profile-page__incomplete-header">
            <span className="profile-page__incomplete-icon">⚠</span>
            <span className="profile-page__incomplete-title">
              Complete your profile to place orders
            </span>
          </div>
          <ul className="profile-page__checklist">
            {completionChecklist.map(({ label, done }) => (
              <li
                key={label}
                className={`profile-page__checklist-item${done ? ' profile-page__checklist-item--done' : ''}`}
              >
                <span className="profile-page__checklist-icon">{done ? '✓' : '○'}</span>
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="profile-page__alert">
          <Alert type="success">{successMessage}</Alert>
        </div>
      )}
      {errorMessage && (
        <div className="profile-page__alert">
          <Alert type="error">{errorMessage}</Alert>
        </div>
      )}

      {/* Profile Form */}
      <div className="profile-page__content profile-page__content--form">
        <form className="profile-page__form" onSubmit={handleSubmit}>
          {/* Account Information */}
          <div className="profile-page__section">
            <h3 className="profile-page__section-title">Account Information</h3>

            <div className="profile-page__field-group">
              <div className="profile-page__field-readonly">
                <label className="profile-page__label">Email Address</label>
                <div className="profile-page__value">{user.email}</div>
                <p className="profile-page__help-text">
                  Contact support to change your email address
                </p>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="profile-page__section">
            <h3 className="profile-page__section-title">Personal Information</h3>

            <div className="profile-page__row">
              <Input
                type="text"
                label="First Name"
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                error={errors.first_name}
                required
              />

              <Input
                type="text"
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                error={errors.last_name}
                required
              />
            </div>
          </div>

          {/* Company Information */}
          <div className="profile-page__section">
            <h3 className="profile-page__section-title">Company Information</h3>
            <p className="profile-page__section-note">
              Changes here apply to all users on your account.
            </p>

            <Input
              type="text"
              label="Company Name"
              value={formData.company_name}
              onChange={(e) => handleChange('company_name', e.target.value)}
              error={errors.company_name}
              required
            />

            <Input
              type="tel"
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => handleChange('phone', formatPhoneInput(e.target.value))}
              error={errors.phone}
              placeholder="555-555-5555"
              maxLength={12}
            />

            {/* Business Types */}
            <div className="profile-page__field-group">
              <label className="profile-page__label">Business Type</label>
              <div className="profile-page__checkboxes">
                {BUSINESS_TYPE_OPTIONS.map((type) => (
                  <label key={type} className="profile-page__checkbox-label">
                    <input
                      type="checkbox"
                      className="profile-page__checkbox"
                      checked={formData.business_types.includes(type)}
                      onChange={() => handleBusinessTypeToggle(type)}
                    />
                    <span>{type}</span>
                  </label>
                ))}
              </div>
              {formData.business_types.includes('Other') && (
                <Input
                  type="text"
                  label="Please describe your business"
                  value={formData.business_type_other}
                  onChange={(e) => handleChange('business_type_other', e.target.value)}
                  error={errors.business_type_other}
                  placeholder="e.g. Food truck, catering, farmers market vendor…"
                  required
                />
              )}
            </div>

            <Input
              type="text"
              label="Resale / Tax ID (optional)"
              value={formData.resale_tax_id}
              onChange={(e) => handleChange('resale_tax_id', e.target.value)}
              error={errors.resale_tax_id}
              placeholder="e.g. 123-456-789"
            />
          </div>

          {/* Form Actions */}
          <div className="profile-page__actions">
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>

      {/* Billing Contacts */}
      <div className="profile-page__content profile-page__content--billing-contacts">
        <div className="profile-page__section">
            <h3 className="profile-page__section-title">Billing Contacts</h3>
            <p className="profile-page__section-note">
              Who should receive invoices? The primary contact receives Stripe invoice emails directly; others are CC'd.
            </p>

            {contactError && (
              <div className="profile-page__alert">
                <Alert type="error">{contactError}</Alert>
              </div>
            )}

            {billingContacts.length > 0 && (
              <div className="profile-page__billing-contacts">
                {billingContacts.map((contact) => (
                  <div key={contact.id} className="profile-page__billing-contact">
                    <div className="profile-page__billing-contact-info">
                      <span className="profile-page__billing-contact-name">{contact.name}</span>
                      <span className="profile-page__billing-contact-email">{contact.email}</span>
                    </div>
                    <div className="profile-page__billing-contact-actions">
                      {contact.is_primary ? (
                        <span className="profile-page__billing-primary-badge">Primary</span>
                      ) : (
                        <button
                          type="button"
                          className="profile-page__billing-action-btn"
                          onClick={() => handleSetPrimary(contact.id)}
                        >
                          Set as Primary
                        </button>
                      )}
                      <button
                        type="button"
                        className="profile-page__billing-action-btn profile-page__billing-action-btn--danger"
                        onClick={() => handleDeleteContact(contact.id)}
                        disabled={contact.is_primary || billingContacts.length <= 1}
                        title={contact.is_primary ? 'Set another contact as primary before removing this one' : ''}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showAddContact ? (
              <form className="profile-page__add-contact-form" onSubmit={handleAddContact}>
                <Input
                  type="text"
                  label="Name"
                  value={newContact.name}
                  onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Jane Smith"
                />
                <Input
                  type="email"
                  label="Email"
                  value={newContact.email}
                  onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="billing@yourcompany.com"
                />
                <div className="profile-page__add-contact-actions">
                  <Button type="submit" variant="primary" disabled={addingContact}>
                    {addingContact ? 'Adding...' : 'Add Contact'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => { setShowAddContact(false); setNewContact({ name: '', email: '' }); setContactError(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <button
                type="button"
                className="profile-page__add-contact-btn"
                onClick={() => setShowAddContact(true)}
              >
                + Add Billing Contact
              </button>
            )}
          </div>
        </div>

      {/* Addresses */}
      <div className="profile-page__content profile-page__content--addresses">
        <div className="profile-page__section-header">
          <h3 className="profile-page__section-title">Shipping &amp; Billing Addresses</h3>
          <p className="profile-page__section-note">Shared across all users on your account.</p>
        </div>
        {addressError && (
          <div className="profile-page__alert">
            <Alert type="error">{addressError}</Alert>
          </div>
        )}
        {addressesLoading ? (
          <div className="profile-page__addresses-loading">
            <Loading />
          </div>
        ) : (
          <AddressEditor
            companyId={companyId}
            addresses={addresses}
            onAdd={handleAddAddress}
            onEdit={handleEditAddress}
            onDelete={handleDeleteAddress}
            onSetDefault={handleSetDefault}
            isSaving={isSavingAddress}
          />
        )}
      </div>

      {/* Team Members */}
      <div className="profile-page__content profile-page__content--members">
        <div className="profile-page__section-header profile-page__section-header--row">
          <h3 className="profile-page__section-title">Team Members</h3>
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={() => { setShowInviteForm((v) => !v); setInviteError(''); setInviteSuccess(''); }}
          >
            {showInviteForm ? 'Cancel' : 'Invite'}
          </Button>
        </div>

        {inviteSuccess && (
          <div className="profile-page__alert">
            <Alert type="success">{inviteSuccess}</Alert>
          </div>
        )}

        {showInviteForm && (
          <form className="profile-page__invite-form" onSubmit={handleInviteSubmit}>
            {inviteError && (
              <div className="profile-page__alert">
                <Alert type="error">{inviteError}</Alert>
              </div>
            )}
            <div className="profile-page__invite-fields">
              <Input
                type="text"
                label="First Name"
                value={inviteData.first_name}
                onChange={(e) => setInviteData((d) => ({ ...d, first_name: e.target.value }))}
              />
              <Input
                type="text"
                label="Last Name"
                value={inviteData.last_name}
                onChange={(e) => setInviteData((d) => ({ ...d, last_name: e.target.value }))}
              />
              <Input
                type="email"
                label="Email"
                value={inviteData.email}
                onChange={(e) => setInviteData((d) => ({ ...d, email: e.target.value }))}
                required
              />
            </div>
            <Button type="submit" variant="primary" size="small" disabled={inviteSubmitting}>
              {inviteSubmitting ? 'Sending…' : 'Send Invite'}
            </Button>
          </form>
        )}

        {members.length > 0 && (
          <>
            <p className="profile-page__section-note">
              Other users with access to this account.
            </p>
            <ul className="profile-page__members-list">
              {members.map((m) => {
                const isMe = m.email === user.email;
                const fullName = [m.first_name, m.last_name].filter(Boolean).join(' ') || '—';
                return (
                  <li key={m.id} className="profile-page__member">
                    <span className="profile-page__member-name">
                      {fullName}
                      {isMe && <span className="profile-page__member-you">You</span>}
                    </span>
                    <span className="profile-page__member-email">{m.email}</span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {/* Change Password */}
      <div className="profile-page__content profile-page__content--password">
        <form className="profile-page__form" onSubmit={handlePasswordChange}>
          <div className="profile-page__section">
            <h3 className="profile-page__section-title">Change Password</h3>

            {pwSuccess && (
              <div className="profile-page__alert">
                <Alert type="success">{pwSuccess}</Alert>
              </div>
            )}
            {pwError && (
              <div className="profile-page__alert">
                <Alert type="error">{pwError}</Alert>
              </div>
            )}

            <Input
              type="password"
              name="current_password"
              label="Current Password"
              value={pwData.current}
              onChange={(e) => {
                setPwData(prev => ({ ...prev, current: e.target.value }));
                if (pwErrors.current) setPwErrors(prev => { const p = { ...prev }; delete p.current; return p; });
                if (pwError) setPwError('');
                if (pwSuccess) setPwSuccess('');
              }}
              error={pwErrors.current}
              autoComplete="current-password"
              required
            />

            <Input
              type="password"
              name="new_password"
              label="New Password"
              value={pwData.newPw}
              onChange={(e) => {
                setPwData(prev => ({ ...prev, newPw: e.target.value }));
                if (pwErrors.newPw) setPwErrors(prev => { const p = { ...prev }; delete p.newPw; return p; });
                if (pwError) setPwError('');
                if (pwSuccess) setPwSuccess('');
              }}
              error={pwErrors.newPw}
              autoComplete="new-password"
              required
            />

            <Input
              type="password"
              name="confirm_password"
              label="Confirm New Password"
              value={pwData.confirm}
              onChange={(e) => {
                setPwData(prev => ({ ...prev, confirm: e.target.value }));
                if (pwErrors.confirm) setPwErrors(prev => { const p = { ...prev }; delete p.confirm; return p; });
                if (pwError) setPwError('');
                if (pwSuccess) setPwSuccess('');
              }}
              error={pwErrors.confirm}
              autoComplete="new-password"
              required
            />
          </div>

          <div className="profile-page__actions">
            <Button type="submit" variant="primary" disabled={pwSubmitting}>
              {pwSubmitting ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
